import { DocumentationChecklistList } from "@/components/documentation-checklists/documentation-checklist-list";
import { Content } from "@/components/layouts/content";
import { SiteDocumentationList } from "@/components/site-documentations/site-documentation-list";
import siteService from "@/features/dashboard/api/services/site-service";
import type { Site } from "@/features/dashboard/types/site";
import type { SiteDocumentation } from "@/features/dashboard/types/site-documentation";
import { IconActivity, IconCalendar, IconCode, IconMapPin } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/_dashboard/sites_/$siteId")({
	component: Page,
});

function Page() {
	const { siteId } = Route.useParams();
	const [selectedDocumentation, setSelectedDocumentation] = useState<SiteDocumentation | null>(null);

	const siteQuery = useQuery({
		queryKey: ["site", siteId],
		queryFn: () => siteService.findById(siteId),
	});

	const site = siteQuery.data;

	if (siteQuery.isLoading) {
		return (
			<Content pageName="Site Details">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading site details...</p>
					</div>
				</div>
			</Content>
		);
	}

	if (!site) {
		return (
			<Content pageName="Site Details">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<p className="text-lg text-gray-600">Site not found</p>
					</div>
				</div>
			</Content>
		);
	}

	return (
		<Content pageName={`Site: ${site.site_code}`}>
			<div className="flex flex-col w-full h-full">
				{/* Upper part - Site Details */}
				<div className="flex-shrink-0 bg-white border-b">
					<div className="p-6">
						<div className="flex items-start justify-between mb-6">
							<div>
								<h1 className="text-2xl font-bold text-gray-900">{site.site_code}</h1>
								<p className="text-sm text-gray-500 mt-1">{site.description || "No description"}</p>
							</div>
							<div className="flex items-center gap-2">
								<span className={`px-3 py-1 rounded-full text-xs font-medium ${site.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}>
									{site.is_active ? "Active" : "Inactive"}
								</span>
							</div>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
							<InfoCard icon={<IconCode className="w-5 h-5" />} label="Kit Number" value={site.kit_number || "-"} />
							<InfoCard
								icon={<IconMapPin className="w-5 h-5" />}
								label="Location"
								value={`${site.locality || "-"}, ${site.province || "-"}, ${site.region || "-"}`}
							/>
							<InfoCard icon={<IconActivity className="w-5 h-5" />} label="Project" value={site.project_name ? `${site.project_name}` : "-"} />
							<InfoCard
								icon={<IconCalendar className="w-5 h-5" />}
								label="Long/Lat"
								value={site.longitude && site.latitude ? `${site.longitude}, ${site.latitude}` : "-"}
							/>
						</div>
					</div>
				</div>

				<div className="flex-1 flex overflow-hidden">
					<div className="w-[30%] border-r bg-gray-50 overflow-y-auto">
						<div className="p-6 space-y-6">
							<div className="border-t pt-6">
								<SiteDocumentationList
									siteId={siteId}
									siteCode={site.site_code}
									onDocumentationSelect={setSelectedDocumentation}
									selectedDocumentationId={selectedDocumentation?.id || null}
								/>
							</div>
						</div>
					</div>

					<div className="flex-1 bg-white overflow-y-auto">
						<div className="p-6">
							{selectedDocumentation ? (
								<DocumentationChecklistList
									documentationId={selectedDocumentation.id || ""}
									categoryName={`${selectedDocumentation.category} (${selectedDocumentation.mir_count})`}
								/>
							) : (
								<div className="flex items-center justify-center h-full">
									<div className="text-center">
										<div className="text-gray-400 mb-4">
											<IconActivity className="w-12 h-12 mx-auto" />
										</div>
										<h3 className="text-lg font-medium text-gray-900 mb-2">Select Documentation</h3>
										<p className="text-sm text-gray-500">Choose a documentation from the left to view its checklists</p>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</Content>
	);
}

function InfoCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
	return (
		<div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
			<div className="text-gray-400">{icon}</div>
			<div>
				<p className="text-xs text-gray-500">{label}</p>
				<p className="text-sm font-semibold text-gray-900">{value}</p>
			</div>
		</div>
	);
}

function DetailItem({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<p className="text-xs text-gray-500">{label}</p>
			<p className="text-sm font-medium text-gray-900">{value}</p>
		</div>
	);
}
